import React from "react";
import { Link } from "react-router-dom";

const sectionLinkClass = "text-blue-600 font-semibold hover:underline";

// Registry of feature-overview topics. Add an entry here to add a new
// help topic - the overview index and section pages both read from this
// list, so nothing else needs to change to add/reorder/expand a topic.
const sections = [
  {
    slug: "why-jokelab",
    title: "Why JokeLab?",
    summary: "What JokeLab is for.",
    content: (
      <>
        <p>
          JokeLab is a tool for writing and storing standup comedy. It's a
          place to draft bits, keep every past version of a set, structure
          and time it out, and rehearse it.
        </p>
        <p>
          It also gives feedback on where a bit is likely to land with an
          audience and how it plays overall. Grr can find laughs, break
          content into bits, and provide reviews. That's just one
          perspective, not a substitute for a real audience, but it's a
          useful gut check to keep you on track as you write. Grr can be
          disabled entirely in{" "}
          <Link to="/help/overview?section=settings" className={sectionLinkClass}>
            Settings
          </Link>{" "}
          if you'd rather not use it.
        </p>
      </>
    ),
  },
  {
    slug: "journal",
    title: "Journal",
    summary: "A daily writing habit tracker, separate from your scripts.",
    content: (
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <p className="flex-1 m-0">
          A daily journal, separate from your scripts, for jotting down ideas
          as they come to you. A card on the index page tracks your entry
          count and your current and longest daily streak.
        </p>
        <img
          src="/journal-card.png"
          alt="Daily Journal card on the index page showing entry count, current streak, and longest streak"
          className="w-full sm:w-[220px] flex-shrink-0 rounded border border-gray-200"
        />
      </div>
    ),
  },
  {
    slug: "modes",
    title: "Write, Practice & Perform modes",
    summary: "The three views the editor's mode toggle switches between.",
    content: (
      <>
        <p>
          Every document has three modes, switched with the toggle in the
          editor toolbar.
        </p>
        <div className="flex flex-col gap-3">
          <p className="m-0">
            <b>Write Mode</b> is where you draft and rewrite. Its
            formatted view shows bit headers, bold punchlines, and italic
            stage directions as you type, and each bit shows a running
            time estimate. See{" "}
            <Link to="/help/overview?section=formatted-view" className={sectionLinkClass}>
              Formatted View
            </Link>{" "}
            for details.
          </p>
        </div>
        <hr className="border-gray-200" />
        <div className="flex flex-col gap-3">
          <p className="m-0">
            <b>Practice Mode</b> is a fullscreen teleprompter for rehearsing out
            loud. The whole script scrolls continuously at your speaking
            pace: the line you should be saying is marked with a bar on the
            third line, with the two lines you just said above it. Click any
            line to jump there, use the arrows to jump between bits, and
            adjust the playback speed below.
          </p>
          <img
            src="/practice-mode.png"
            alt="Practice mode showing a teleprompter-style view of one bit with playback controls"
            className="w-full rounded border border-gray-200"
          />
        </div>
        <hr className="border-gray-200" />
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="flex-1 m-0">
            <b>Perform Mode</b> turns your script into a collapsible set list —
            each bit expands or collapses — built for quick reference on your
            phone.
          </p>
          <img
            src="/perform-mode.png"
            alt="Perform mode showing a collapsible set list with bit timings and total time"
            className="w-[140px] flex-shrink-0 rounded border border-gray-200 self-start"
          />
        </div>
      </>
    ),
  },
  {
    slug: "formatted-view",
    title: "Formatted View",
    summary: "Formatting your set as you write, with live bit timings.",
    content: (
      <>
        <p>
          Write mode's formatted view shows your set the way it'll
          read back — you don't see the{" "}
          <Link to="/help/overview?section=markup" className={sectionLinkClass}>
            markup
          </Link>{" "}
          characters, just the result:
        </p>
        <ul className="list-disc list-inside">
          <li>Bit headers appear as labeled bars across the page.</li>
          <li>Punchlines appear in <b>bold</b>.</li>
          <li>
            Stage directions appear in{" "}
            <i className="text-gray-500">gray italics</i>.
          </li>
        </ul>
        <p>
          The toolbar under the menu (and the matching <b>Edit</b> menu)
          applies formatting for you:
        </p>
        <ul className="list-disc list-inside">
          <li>
            <b>Punchline</b> — select a word or phrase and click it to mark
            it as a punchline. Select any part of an existing punchline
            and click it again to unmark it.
          </li>
          <li>
            <b>Bit</b> — turns the line your cursor is on into a bit header,
            or back into plain text.
          </li>
          <li>
            <b>Stage</b> — select text and click it to mark it as a stage
            direction. Select any part of an existing one and click it
            again to unmark it.
          </li>
        </ul>
        <p>
          You can also just type the markup — starting a line with{" "}
          <code>==</code> turns it into a bit header, and <code>*word</code>,{" "}
          <code>(a few words)</code>, and <code>[a note]</code> format
          themselves the same way.
        </p>
        <p>
          <b>Bit timings:</b> each bit header shows its start and end time in
          the set (e.g. <code>0:45–1:30</code>), updated as you type. It's
          based on the words-per-minute pace in{" "}
          <Link to="/help/overview?section=settings" className={sectionLinkClass}>
            Settings
          </Link>
          , and stage directions don't count toward it — the same estimate
          Perform mode uses.
        </p>
        <p>
          <b>Copy &amp; paste:</b> copying from the formatted view gives you clean,
          readable text for pasting elsewhere, without any markup. Pasting
          it back into JokeLab keeps its formatting. Undo and redo
          (Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z) work as usual.
        </p>
        <div className="flex flex-col gap-3">
          <p className="m-0">
            <b>Markup view:</b> to see and edit the markup directly, choose{" "}
            <b>View → Markup</b>. <b>View → Formatted</b> switches
            back. Both views edit the same text.
          </p>
          <img
            src="/write-mode.png"
            alt="Markup view showing plain text with == bit markers and *word / (phrase) punchline markup"
            className="w-full rounded border border-gray-200"
          />
        </div>
      </>
    ),
  },
  {
    slug: "grr-review",
    title: "Grr Review",
    summary: "The coach critique — rating, laugh counts, and feedback.",
    content: (
      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
        <div className="flex-1">
          <p>
            <b>Grr Review</b>, from the{" "}
            <Link to="/help/overview?section=grrr-menu" className={sectionLinkClass}>
              Grrr menu
            </Link>
            , is a critique of the routine you've written — it doesn't
            rewrite or generate anything.
          </p>
          <ul className="list-disc list-inside">
            <li>A content rating, like an MPAA movie rating (e.g. PG-13).</li>
            <li>
              A <b>Best Part</b> callout naming the strongest bit and why it
              works.
            </li>
            <li>
              An <b>Areas for Improvement</b> callout naming what needs work.
            </li>
            <li>
              A <b>Confusing Part</b> callout, shown only on the rare bit
              where a setup and punchline don't logically connect.
            </li>
            <li>
              A laugh count broken into three tiers — <b>OK</b>, <b>Good</b>,
              and <b>Great</b> — based on how hard each moment is expected to
              land.
            </li>
          </ul>
        </div>
        <img
          src="/grr-review.png"
          alt="Grr Review modal showing a PG-13 content rating, Best Part and Areas for Improvement callouts, and OK/Good/Great laugh counts"
          className="w-full sm:w-[260px] flex-shrink-0 rounded border border-gray-200"
        />
      </div>
    ),
  },
  {
    slug: "markup",
    title: "Documents & Markup",
    summary: "Bits, punchlines, and stage directions are all markup.",
    content: (
      <>
        <p>
          A document is one standup script. Its bits, punchlines, and stage
          directions are all marked up right in the text. The{" "}
          <Link to="/help/overview?section=formatted-view" className={sectionLinkClass}>
            formatted view
          </Link>{" "}
          hides the markup and shows the formatting instead, but you can
          still type it directly, or see it with <b>View → Markup</b>:
        </p>
        <ul className="list-disc list-inside">
          <li>
            <code>==</code> starts a bit (a topic or story) — e.g.{" "}
            <code>==Airport security</code>. Everything until the next{" "}
            <code>==</code> line belongs to that bit.
          </li>
          <li><code>*word</code> marks a single-word punchline.</li>
          <li><code>(a few words)</code> marks a multi-word punchline.</li>
          <li>
            <code>[stage direction]</code> notes a physical beat or delivery
            note. It's shown in italics and doesn't count toward word count
            or timing.
          </li>
        </ul>
      </>
    ),
  },
  {
    slug: "grrr-menu",
    title: "Grrr Menu",
    summary: "For importing/editing text you've written, and feedback on it.",
    content: (
      <>
        <p>
          Grrr helps with your jokes without writing them for you — it
          works with text you've already written, making it faster to
          import and edit, and giving you feedback on it.
        </p>
        <p>The <b>Grrr</b> menu in the editor has three tools:</p>
        <ul className="list-disc list-inside">
          <li>
            <b>Add Bits</b> — for importing a raw, unformatted set: splits it
            into labeled bits so you don't have to add <code>==</code> lines
            by hand.
          </li>
          <li>
            <b>Find Laughs</b> — for editing existing text: marks likely
            punchlines with <code>*</code> and <code>()</code> so you don't
            have to mark them up by hand.
          </li>
          <li>
            <b>Grr Review</b> — a critique of what you've written. See{" "}
            <Link to="/help/overview?section=grr-review" className={sectionLinkClass}>
              its own topic
            </Link>{" "}
            for details.
          </li>
        </ul>
        <p>
          There's also a question box at the bottom of the editor for asking
          about your set — again, it answers questions about the text you
          give it rather than generating new material.
        </p>
        <p>
          <b>Privacy:</b> only the text of the document you're working on (or
          your question) is sent when you use one of these tools — nothing
          runs in the background, and nothing is sent unless you trigger it.
          JokeLab doesn't separately store your prompts or the replies; if
          you want to keep a result, it has to be saved into your document
          like anything else you write. That text is sent to an AI provider
          to generate the response. It isn't used to train the AI's models,
          and is only retained briefly (up to 30 days) for abuse monitoring
          before being deleted.
        </p>
      </>
    ),
  },
  {
    slug: "settings",
    title: "Settings",
    summary: "Your words-per-minute pace, and whether AI is enabled.",
    content: (
      <>
        <p>
          Click your name in the header to open <b>Settings</b>, where you can
          set your words-per-minute speaking pace. It's used to estimate set
          timings throughout the app — on bit headers in the{" "}
          <Link to="/help/overview?section=formatted-view" className={sectionLinkClass}>
            formatted view
          </Link>
          , in{" "}
          <Link to="/help/overview?section=modes" className={sectionLinkClass}>
            Perform mode
          </Link>
          , and in your stats on the index page.
        </p>
        <p>
          Check <b>Disable AI</b> there to turn off Grr — this hides the{" "}
          <Link to="/help/overview?section=grrr-menu" className={sectionLinkClass}>
            Grrr menu
          </Link>{" "}
          and the question box in the editor.
        </p>
      </>
    ),
  },
];

export default sections;
